'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { Accessibility, X } from 'lucide-react';
import Link from '@/core/i18n/navigation';
import { useTranslation } from '@/core/components/providers/TranslationProvider';
import { cn } from '@/core/utils/cn';

/**
 * "Pasek dostępności" — module wcag_toolbar (simple-site §6.3). The visitor's choices are data
 * attributes on <html> styled in app/storefront.css, remembered in this browser only.
 */
type Contrast = 'normal' | 'yellow-black' | 'black-yellow' | 'mono';
interface Prefs { contrast: Contrast; text: number; spacing: boolean; links: boolean }

const DEFAULTS: Prefs = { contrast: 'normal', text: 100, spacing: false, links: false };
const STORAGE_KEY = 'stag-a11y';
const TEXT_STEPS = [100, 125, 150, 175, 200];

function apply(prefs: Prefs) {
  const html = document.documentElement;
  const set = (name: string, value: string | null) => (value ? html.setAttribute(name, value) : html.removeAttribute(name));
  set('data-a11y-contrast', prefs.contrast === 'normal' ? null : prefs.contrast);
  set('data-a11y-text', prefs.text === 100 ? null : String(prefs.text));
  set('data-a11y-spacing', prefs.spacing ? 'on' : null);
  set('data-a11y-links', prefs.links ? 'on' : null);
}

function load(): Prefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : DEFAULTS;
  } catch {
    return DEFAULTS;
  }
}

export function AccessibilityToolbar({ declarationHref = '/deklaracja-dostepnosci' }: { declarationHref?: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState<Prefs>(DEFAULTS);
  const panelId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const saved = load();
    setPrefs(saved);
    apply(saved);
  }, []);

  useEffect(() => {
    if (open) panelRef.current?.querySelector<HTMLElement>('button, a')?.focus();
  }, [open]);

  const update = (patch: Partial<Prefs>) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    apply(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch { /* storage blocked — the choice still applies to this page view */ }
  };

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  const contrastOptions: [Contrast, string][] = [
    ['normal', t('a11y.contrast_normal', 'Normalny')],
    ['yellow-black', t('a11y.contrast_yellow_black', 'Żółty na czarnym')],
    ['black-yellow', t('a11y.contrast_black_yellow', 'Czarny na żółtym')],
    ['mono', t('a11y.contrast_mono', 'Monochromatyczny')],
  ];
  const chip = (active: boolean) => cn(
    'rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition',
    active ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 bg-white text-slate-800 hover:border-slate-500',
  );

  return (
    <div className="a11y-toolbar-root fixed bottom-4 left-4 z-[9990] print:hidden">
      {open && (
        <div
          ref={panelRef}
          id={panelId}
          role="dialog"
          aria-label={t('a11y.toolbar', 'Ułatwienia dostępu')}
          onKeyDown={(e) => { if (e.key === 'Escape') close(); }}
          className="a11y-toolbar-panel mb-3 w-[min(20rem,calc(100vw-2rem))] space-y-4 rounded-2xl border border-slate-200 bg-white p-4 text-slate-900 shadow-2xl"
        >
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold">{t('a11y.toolbar', 'Ułatwienia dostępu')}</h2>
            <button type="button" onClick={close} aria-label={t('a11y.close', 'Zamknij ułatwienia dostępu')} className="rounded p-1 hover:bg-slate-100">
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>

          <fieldset className="space-y-2">
            <legend className="text-xs font-semibold text-slate-600">{t('a11y.contrast', 'Kontrast')}</legend>
            <div className="flex flex-wrap gap-1.5">
              {contrastOptions.map(([value, label]) => (
                <button key={value} type="button" aria-pressed={prefs.contrast === value} onClick={() => update({ contrast: value })} className={chip(prefs.contrast === value)}>
                  {label}
                </button>
              ))}
            </div>
          </fieldset>

          <fieldset className="space-y-2">
            <legend className="text-xs font-semibold text-slate-600">{t('a11y.text_size', 'Wielkość tekstu')}</legend>
            <div className="flex flex-wrap gap-1.5">
              {TEXT_STEPS.map((step) => (
                <button key={step} type="button" aria-pressed={prefs.text === step} onClick={() => update({ text: step })} className={chip(prefs.text === step)}>
                  {step}%
                </button>
              ))}
            </div>
          </fieldset>

          <div className="flex flex-wrap gap-1.5">
            <button type="button" aria-pressed={prefs.spacing} onClick={() => update({ spacing: !prefs.spacing })} className={chip(prefs.spacing)}>
              {t('a11y.spacing', 'Większe odstępy')}
            </button>
            <button type="button" aria-pressed={prefs.links} onClick={() => update({ links: !prefs.links })} className={chip(prefs.links)}>
              {t('a11y.underline_links', 'Podkreśl linki')}
            </button>
          </div>

          <div className="flex items-center justify-between border-t border-slate-100 pt-3 text-xs">
            <button type="button" onClick={() => update(DEFAULTS)} className="font-semibold text-slate-700 underline underline-offset-2">
              {t('a11y.reset', 'Przywróć domyślne')}
            </button>
            <Link href={declarationHref} className="font-semibold text-slate-700 underline underline-offset-2">
              {t('a11y.declaration', 'Deklaracja dostępności')}
            </Link>
          </div>
        </div>
      )}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={t('a11y.toolbar', 'Ułatwienia dostępu')}
        className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-900 text-white shadow-lg hover:bg-slate-700"
      >
        <Accessibility className="h-6 w-6" aria-hidden />
      </button>
    </div>
  );
}
